import {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { BlockHandle, BlockProps } from "./utils";
import BlockContainer from "./BlockContainer";
import TaskAgentDisplay from "../../tools/TaskAgentDisplay";
import {
  useTaskAgentState,
  processEventsWithAddedData,
  BackendDelegationTask,
  DelegationOpportunity,
} from "../../hooks/useTaskAgentState";
import { useWebSocketContext } from "../../context/SocketConsumerProvider";
import {
  useAgentMessage,
  useGetAgentsThread,
  useInterruptAgentThread,
  useRetryAgentThread,
} from "../../../services/agents";
import { ChevronUp, ChevronDown, Trash2 } from "lucide-react";
import BlockGutterMap from "./BlockGutterMap";
import {
  useUpdateBlockMetadata,
  useUpdateTaskBlockStatusRequest,
  useCreateTaskBlock,
} from "services/tasks";
import { Collapse } from "@mui/material";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";
import { GetAgentsThreadRequest } from "protogen/agents_service_pb";
import { RichContent } from "components/editor/utils";
import DateDisplay from "../../common/DateDisplay";
import { TaskBlockStatus } from "protogen/tasks_pb";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "../../common/ConfirmationDialog";
import WithDividers from "components/helpers/WithDividers";
import LinkRouter from "components/navigation/LinkRouter";
import useIsMobile from "components/hooks/useIsMobile";
import AgentDebugToolButton from "./AgentDebugToolButton";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import { getScrollableParent } from "../workflowStates";
import { useScrollOnExpand } from "components/hooks/useScrollOnExpand";
import { parseDelegationOpportunityFromFunctionCall } from "types/delegation";

const FEEDBACK_FORM_UUID = "5b91e7db-7593-4568-b83f-96b041c68143";

const AgentBlock = forwardRef<BlockHandle, BlockProps>(
  (
    {
      block,
      task,
      accountType = "advisor",
      onDeleteTaskBlock,
      refreshTask,
      delegationTasks,
    },
    ref,
  ) => {
    const metadata = block.metadata ? JSON.parse(block.metadata) : {};
    const threadRef = block.agentThreadRef || "";
    const lightningAgentThreadRef = block.lightningAgentThreadRef || "";
    const isMobile = useIsMobile();
    // State management: show content when we have a thread ref (e.g. reused lightning thread may already have run)
    const [hasActivated, setHasActivated] = useState(!!threadRef);
    const [currentThreadRef, setCurrentThreadRef] = useState<string | null>(
      threadRef || null,
    );
    const [parsedMetadata, setParsedMetadata] = useState<any>(metadata);
    const [isInterrupting, setIsInterrupting] = useState(false);
    const [currentActionType, setCurrentActionType] = useState<
      "updateClientDetails" | "followUpQuestion" | null
    >(null);
    const hasLoadedThread = useRef(false);
    const hasLoadedLightningThread = useRef(false);
    const processedEventCount = useRef(0);
    // const websocketEventHashes = useRef(new Set<string>());

    // Hooks
    const currentUser = useContext(CurrentUserContext);
    const isAdmin = isUserAdmin(currentUser);
    const { registerHandler, unregisterHandler } = useWebSocketContext();
    const { request: agentMessage } = useAgentMessage();
    const { request: getAgentsThread } = useGetAgentsThread();
    const { request: interruptAgentThread } = useInterruptAgentThread();
    const { request: retryAgentThread } = useRetryAgentThread();
    const { request: updateMetadataRequest } = useUpdateBlockMetadata((r) => {
      if (!r.block?.metadata) return;
      setParsedMetadata(JSON.parse(r.block?.metadata));
    });
    const { request: updateBlockStatus } = useUpdateTaskBlockStatusRequest();
    const { request: createTaskBlockRequest } = useCreateTaskBlock();
    const confirmState = useConfirmationDialog();
    const { pushSnack } = useSnackpack();
    const stateManagerRef = useRef<any>(null);
    const { blockRef: scrollRef, handleExpandWithScroll } = useScrollOnExpand();
    const processEvents = useCallback(
      (
        events: string[],
        addedEventsMap: Map<
          number,
          {
            eventRef: string;
            eventStatus: string;
            eventModified: boolean;
            sourceEntityType: string;
            sourceEntityId: string;
            sourceEntityIndex: number;
          }
        >,
      ) => {
        const processedEvents = processEventsWithAddedData(
          events,
          addedEventsMap,
        );
        processedEvents.forEach((eventString: string) => {
          const event = JSON.parse(eventString);
          stateManagerRef.current?.actions.handleWebSocketMessage("", event);
        });
      },
      [],
    );

    const stateManager = useTaskAgentState({
      staticState: { isActive: true },
      accountType,
      onRefresh: async () => {
        if (!threadRef) return;

        const response = await getAgentsThread(
          new GetAgentsThreadRequest({ threadRef }),
        );

        if (response?.events) {
          const allEvents = response.events;

          if (allEvents.length > processedEventCount.current) {
            const newEvents = allEvents.slice(processedEventCount.current);

            const addedEventsMap = new Map<
              number,
              {
                eventRef: string;
                eventStatus: string;
                eventModified: boolean;
                sourceEntityType: string;
                sourceEntityId: string;
                sourceEntityIndex: number;
              }
            >();
            if (response.addedEvents && response.addedEvents.length > 0) {
              response.addedEvents.forEach((addedEvent) => {
                const relativeIndex =
                  addedEvent.sourceEntityIndex - processedEventCount.current;
                if (relativeIndex >= 0) {
                  addedEventsMap.set(relativeIndex, {
                    eventRef: addedEvent.eventRef,
                    eventStatus: addedEvent.eventStatus,
                    eventModified: addedEvent.eventModified,
                    sourceEntityType: addedEvent.sourceEntityType,
                    sourceEntityId: addedEvent.sourceEntityId,
                    sourceEntityIndex: addedEvent.sourceEntityIndex,
                  });
                }
              });
            }

            processEvents(newEvents, addedEventsMap);
            processedEventCount.current = allEvents.length;
          }
        }
      },
    });

    // Store stateManager in ref for processEvents
    stateManagerRef.current = stateManager;

    useImperativeHandle(ref, () => ({
      isPolling: () => false,
    }));

    useEffect(() => {
      const websocketHandler = (channel: string, data: any) => {
        // const eventHash = JSON.stringify({
        //   eventType: data.eventType,
        //   content: data.content,
        //   metadata: data.metadata,
        // });

        // This occurs if we manually refreshed from the API and we
        // want to prevent duplicates if the WS comes back.
        // if (websocketEventHashes.current.has(eventHash)) {
        //   return;
        // }

        // websocketEventHashes.current.add(eventHash);
        stateManager.actions.handleWebSocketMessage(channel, data);
      };

      const channel = `task-agent/${encodeURIComponent(threadRef)}`;
      registerHandler(channel, websocketHandler);

      return () => {
        unregisterHandler(channel, websocketHandler);
      };
    }, [
      registerHandler,
      unregisterHandler,
      threadRef,
      stateManager.actions.handleWebSocketMessage,
    ]);

    // Track activation state
    useEffect(() => {
      if (stateManager.state.isActive) {
        setHasActivated(true);
      } else {
        setCurrentActionType(null);
      }
    }, [stateManager.state.isActive]);

    // Merge delegation tasks from props when they change
    useEffect(() => {
      if (delegationTasks && delegationTasks.length > 0) {
        stateManagerRef.current?.actions.mergeDelegationTasks(
          delegationTasks as BackendDelegationTask[],
        );
      }
    }, [delegationTasks]);

    useEffect(() => {
      const loadThreadState = async (threadRef: string) => {
        if (hasLoadedThread.current) return;
        hasLoadedThread.current = true;

        const response = await getAgentsThread(
          new GetAgentsThreadRequest({ threadRef }),
        );
        stateManagerRef.current?.actions.updateState({
          clientOutputVisible: response?.clientOutputVisible || false,
          playbookRef: response?.playbookRef || undefined,
        });

        const addedEventsMap = new Map<
          number,
          {
            eventRef: string;
            eventStatus: string;
            eventModified: boolean;
            sourceEntityType: string;
            sourceEntityId: string;
            sourceEntityIndex: number;
          }
        >();
        if (response?.addedEvents && response.addedEvents.length > 0) {
          response.addedEvents.forEach((addedEvent) => {
            addedEventsMap.set(addedEvent.sourceEntityIndex, {
              eventRef: addedEvent.eventRef,
              eventStatus: addedEvent.eventStatus,
              eventModified: addedEvent.eventModified,
              sourceEntityType: addedEvent.sourceEntityType,
              sourceEntityId: addedEvent.sourceEntityId,
              sourceEntityIndex: addedEvent.sourceEntityIndex,
            });
          });
        }

        const events = response?.events || [];
        processEvents(events, addedEventsMap);
        processedEventCount.current = events.length;

        setHasActivated(true);
        setCurrentThreadRef(threadRef);
      };

      if (threadRef) {
        loadThreadState(threadRef);
      }
    }, [threadRef, getAgentsThread]);

    useEffect(() => {
      if (!lightningAgentThreadRef || hasLoadedLightningThread.current) return;
      hasLoadedLightningThread.current = true;
      getAgentsThread(
        new GetAgentsThreadRequest({ threadRef: lightningAgentThreadRef }),
      ).then((response) => {
        const events = response?.events || [];
        const opportunities: DelegationOpportunity[] = [];
        events.forEach((eventStr: string) => {
          const event = JSON.parse(eventStr);
          if (event.eventType === "function_call_completed" && event.content) {
            const data = JSON.parse(event.content);
            const opportunity =
              parseDelegationOpportunityFromFunctionCall(data);
            if (opportunity) {
              opportunities.push({
                ...opportunity,
                agentThreadRef: lightningAgentThreadRef,
              });
            }
          }
        });
        if (opportunities.length > 0 && stateManagerRef.current) {
          const prev = stateManagerRef.current.state.delegationOpportunities;
          stateManagerRef.current.actions.updateState({
            delegationOpportunities: [
              ...prev,
              {
                id: "lightning-group",
                opportunities,
                timestamp: Date.now(),
              },
            ],
          });
        }
      });
    }, [lightningAgentThreadRef, getAgentsThread]);

    // Handler functions
    const handleUpdatePlanDetails = async (
      content: RichContent,
      _details: any[],
    ) => {
      if (!currentThreadRef) {
        console.error("No thread reference available");
        return;
      }

      // Set action type for loading animation
      setCurrentActionType("updateClientDetails");

      // Mark agent as active so it processes the new information
      stateManager.actions.updateState({
        isActive: true,
        lastEventTime: Date.now(),
      });

      // Send the details as a message to the agent WITHOUT showing in chat
      await agentMessage({
        threadRef: currentThreadRef,
        body: {
          contentType: "html",
          payload: content.html,
          textContent: content.text,
          attachments: content.attachments,
        },
        model: "",
        hideFromChat: true,
      });
    };

    const handleFollowUpQuestion = async (content: RichContent) => {
      if (!currentThreadRef) {
        console.error("No thread reference available");
        return;
      }

      // Set action type for loading animation
      setCurrentActionType("followUpQuestion");

      // Immediately add the user message to the chat display
      // Use text version for immediate display (cleaner), while storing HTML
      stateManager.actions.updateState({
        chatMessages: [
          ...stateManager.state.chatMessages,
          { content: content.text, contentHtml: content.html, source: "user" },
        ],
        isActive: true,
        lastEventTime: Date.now(),
      });

      // Send the follow-up question as a new message in the same thread
      await agentMessage({
        threadRef: currentThreadRef,
        body: {
          contentType: "html",
          payload: content.html, // Send HTML for rich formatting and attachments
          textContent: content.text,
          attachments: content.attachments,
        },
        model: "",
        hideFromChat: false,
      });
    };

    const handleInterrupt = async () => {
      if (!currentThreadRef || isInterrupting) {
        return;
      }

      setIsInterrupting(true);

      stateManager.actions.updateState({
        isActive: false,
        hasCompleted: true,
      });

      // Minimum delay to prevent jarring UI flash
      await new Promise((resolve) => setTimeout(resolve, 800));

      await interruptAgentThread({
        threadRef: currentThreadRef,
      });

      const response = await getAgentsThread(
        new GetAgentsThreadRequest({ threadRef: currentThreadRef }),
      );

      if (response?.events) {
        processedEventCount.current = 0;

        const addedEventsMap = new Map<
          number,
          {
            eventRef: string;
            eventStatus: string;
            eventModified: boolean;
            sourceEntityType: string;
            sourceEntityId: string;
            sourceEntityIndex: number;
          }
        >();
        if (response.addedEvents && response.addedEvents.length > 0) {
          response.addedEvents.forEach((addedEvent) => {
            addedEventsMap.set(addedEvent.sourceEntityIndex, {
              eventRef: addedEvent.eventRef,
              eventStatus: addedEvent.eventStatus,
              eventModified: addedEvent.eventModified,
              sourceEntityType: addedEvent.sourceEntityType,
              sourceEntityId: addedEvent.sourceEntityId,
              sourceEntityIndex: addedEvent.sourceEntityIndex,
            });
          });
        }

        stateManagerRef.current?.actions.resetState();
        processEvents(response.events, addedEventsMap);

        stateManagerRef.current?.actions.updateState({
          isActive: false,
          hasCompleted: true,
        });

        processedEventCount.current = response.events.length;
      }

      setIsInterrupting(false);
    };

    const handleRetry = async () => {
      if (!currentThreadRef) return;
      stateManager.actions.updateState({
        hasFailed: false,
        isActive: true,
        lastEventTime: Date.now(),
      });
      await retryAgentThread({ threadRef: currentThreadRef });
    };

    // Get family info from task
    const familyRef = task?.family?.ref;

    // Determine if we should show content
    const hasAgentContent = threadRef && hasActivated;

    // Handle collapse toggle
    const handleToggleCollapsed = () => {
      const isCurrentlyCollapsed = parsedMetadata.isCollapsed;

      if (isCurrentlyCollapsed) {
        handleExpandWithScroll(() => {
          updateMetadataRequest({
            taskBlockRef: block.ref,
            key: "isCollapsed",
            value: "false",
          });
        });
      } else {
        updateMetadataRequest({
          taskBlockRef: block.ref,
          key: "isCollapsed",
          value: "true",
        });
      }
    };

    // Handle delete
    const handleDelete = () => {
      confirmState.openDialog(() => {
        updateBlockStatus({
          taskBlockRef: block.ref,
          status: TaskBlockStatus.DELETED,
        });
        onDeleteTaskBlock && onDeleteTaskBlock(block.ref);
      });
    };

    const handleCreateDocumentFromMessage = async (content: string) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, "text/html");
      const textContent = doc.body.textContent || "";

      const result = await createTaskBlockRequest({
        taskRef: task.ref,
        type: "document",
        content: content,
        textContent: textContent.trim(),
        attachments: [],
        contentType: "html",
        metadata: JSON.stringify({
          isShared: false,
          isRichDocument: true,
        }),
      });

      if (result?.block) {
        refreshTask && (await refreshTask());
        pushSnack({
          message: "Document created!",
          alertSeverity: "success",
          key: "document-created",
        });
        // Scroll to the end of the task details panel
        const taskDetailsPanel = document.getElementById("task-details-panel");
        const scrollableParent = getScrollableParent(
          taskDetailsPanel || undefined,
        );
        scrollableParent.scrollTo({
          top: scrollableParent.scrollHeight,
          behavior: "smooth",
        });
      }
    };

    // Update parsed metadata when block metadata changes
    useEffect(() => {
      if (block.metadata) {
        setParsedMetadata(JSON.parse(block.metadata));
      }
    }, [block.metadata]);

    const isMemberVisible = parsedMetadata?.isMemberVisible || false;

    return (
      <BlockContainer
        // Wait to determine what to tell advisors until we have picked a playbook in planning
        hideBottomBanner={stateManager.computed.planWithActions.length === 0}
        bannerText={
          accountType === "advisor"
            ? stateManager.state.clientOutputVisible
              ? "Clients can see and update this section."
              : "Agent output is not visible to clients."
            : undefined
        }
        ref={scrollRef}
        gutterIcon={BlockGutterMap.agent.icon}
        advisorViewOnly={!isMemberVisible}
        visibilityCTA={
          accountType === "advisor" && (
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <WithDividers
                DividerComponent={() =>
                  isMobile ? null : <span>{" • "}</span>
                }
              >
                {isAdmin && <AgentDebugToolButton threadRef={threadRef} />}
                <LinkRouter
                  to={`/forms/complete/${FEEDBACK_FORM_UUID}?Task+Ref=${encodeURIComponent(
                    task.ref,
                  )}&Agent+Ref=${encodeURIComponent(threadRef)}`}
                  targetNew={true}
                  inline={true}
                  useColorScheme={true}
                >
                  Feedback?
                </LinkRouter>
                {accountType === "advisor" &&
                  currentUser?.ref === block.createdBy?.ref && (
                    <IconButton sx={{ height: "unset" }} onClick={handleDelete}>
                      <Trash2 size={16} color="#E57171" />
                    </IconButton>
                  )}
              </WithDividers>
            </Box>
          )
        }
      >
        <Box
          sx={{
            padding: "16px 20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              <Typography variant="bodyHeavy" sx={{ fontWeight: "600" }}>
                Faye Task Helper
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "start",
                gap: "8px",
              }}
            >
              {!stateManager.state.isActive && (
                <DateDisplay
                  date={new Date(Number(block?.createdSec) * 1000)}
                />
              )}
              <Box onClick={handleToggleCollapsed} sx={{ cursor: "pointer" }}>
                {parsedMetadata?.isCollapsed ? (
                  <ChevronDown size={24} stroke="#8E9598" />
                ) : (
                  <ChevronUp size={24} stroke="#8E9598" />
                )}
              </Box>
            </Box>
          </Box>
          {stateManager.state.isActive && !stateManager.computed.hasContent && (
            <Typography
              variant="bodySmall"
              sx={{ marginTop: "4px", marginBottom: "12px" }}
            >
              {accountType === "advisor" ? (
                <>
                  We're getting a head start on the task. Stick around to see
                  how we can assist in moving this task forward.
                </>
              ) : (
                <>
                  We're getting a head start on your task. Stick around to
                  refine necessary details that your Advisor may need.
                </>
              )}
            </Typography>
          )}

          <Collapse in={!parsedMetadata?.isCollapsed} sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {hasAgentContent ? (
                <Box sx={{}}>
                  <TaskAgentDisplay
                    wrapInContainer={false}
                    familyRef={familyRef}
                    family={task?.family}
                    agentThreadRef={threadRef}
                    taskRef={task.ref}
                    stateManager={stateManager}
                    onUpdatePlanDetails={handleUpdatePlanDetails}
                    onFollowUpQuestion={handleFollowUpQuestion}
                    onRetry={handleRetry}
                    onInterrupt={handleInterrupt}
                    isInterrupting={isInterrupting}
                    currentActionType={currentActionType}
                    accountType={accountType}
                    onCreateDocument={
                      accountType === "advisor"
                        ? handleCreateDocumentFromMessage
                        : undefined
                    }
                    onAddEvents={() => {}}
                    refreshTask={refreshTask}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    padding: "12px",
                    backgroundColor: "#F8F9FA",
                    borderRadius: "8px",
                    marginTop: "16px",
                  }}
                >
                  <Typography variant="body2">
                    Agent is ready but no thread reference found.
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>
        <ConfirmationDialog
          content="Are you sure you want to remove this agent block?"
          {...confirmState.dialogProps}
        />
      </BlockContainer>
    );
  },
);

AgentBlock.displayName = "AgentBlock";

export default AgentBlock;
